/*
  Component: Blog list — load more
  Responsibility: fill the grid to a full page, on arrival and on every press.

  The button is a real link to a real second page — see the note at the top of
  sections/kl-blog-listing.liquid. Everything here is an improvement on top of that link, and
  it is written so that failing means falling back to it rather than breaking it:

  - Pages are asked for through the Section Rendering API, which is the platform's, not Dawn's.
    Adding section_id to the URL the link already points at returns that one section rendered
    on its own, so the cards that come back are the same cards a full page load would have
    drawn, from the same pagination.
  - The button's own href is what says which page is next, and after each fill it is moved on to
    whatever the last fetched page said comes after it. When that page has no button, the blog
    has run out and the button is removed.
  - If a request fails, the interception gives up for good and the next press navigates. A
    reader is never left with a control that has quietly stopped working.

  ---------------------------------------------------------------------------------------
  WHY A PAGE CAN ARRIVE SHORT, AND WHAT IS DONE ABOUT IT

  With "Leave out the posts in the featured row" turned on — it is off by default — the grid
  drops the posts the row already showed, but Shopify paginates the blog before that happens.
  So a page holding six featured posts renders four cards, and a page whose every post was
  featured renders none at all. Nothing in Liquid can fix that: paginate cannot page a list with
  items removed.

  So it is fixed here. data-kl-blog-load-more-target says how many cards a full screen is, and
  both the first render and every press keep fetching pages until that many have been added or
  the blog runs out. Whole pages are appended, never part of one — taking half a page would
  mean the other half could never be reached again — so a fill can overshoot the target. Ten
  can arrive as fourteen. It cannot arrive as four.

  Without the attribute the target is one, which is the old behaviour exactly: fetch one page,
  and step past any that come back empty.

  ---------------------------------------------------------------------------------------
  HOOKS

    [data-kl-blog-load-more]              the root, carrying the section id to re-render
    [data-kl-blog-load-more-list]         the <ul> to append into
    [data-kl-blog-load-more-button]       the link
    [data-kl-blog-load-more-status]       where the count is announced
    [data-kl-blog-load-more-target]       how many cards make a full screen

  The only things written to the DOM are appended cards, the button's href, one class, one
  aria-disabled and the announcement's text. No layout — assets/kl-blog-list.css owns what
  is-loading looks like.
*/

const BLOG_LOAD_MORE_LOADING_CLASS = 'is-loading';

/*
  How many pages one fill may ask for. Bounded rather than open-ended: stepping past a short
  page is fair, but a blog whose posts are mostly featured must not turn one press into thirty
  requests.
*/
const BLOG_LOAD_MORE_MAX_REQUESTS = 5;

// Pure. Where to ask Shopify for one section of one page, given the page's own URL.
function getBlogSectionUrl(pageUrl, sectionId) {
  const sectionUrl = new URL(pageUrl);

  sectionUrl.searchParams.set('section_id', sectionId);

  return sectionUrl.toString();
}

// Pure. DOM-free string handling, kept out of the fetch below so it can be reasoned about.
function getBlogStatusMessage(templateText, addedCount) {
  return templateText.replace('[count]', String(addedCount));
}

/*
  Pure. How many more cards a fill still owes, given what is on the page already. Never
  negative — a grid that is already full asks for nothing.
*/
function getBlogCardsWanted(targetCount, presentCount) {
  const wantedCount = targetCount - presentCount;

  return wantedCount > 0 ? wantedCount : 0;
}

/*
  Fetches one page and reports what was on it. Nothing here is appended or otherwise written to
  the live document — the caller does that — so a failure part-way through leaves the page
  exactly as it was.

  The next page's URL is resolved against the one just fetched, because paginate.next.url is a
  path rather than a whole address and needs a base to become one.
*/
async function fetchBlogPage(pageUrl, sectionId) {
  const response = await fetch(getBlogSectionUrl(pageUrl, sectionId));

  if (!response.ok) throw new Error(`Shopify answered ${response.status}`);

  const nextDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
  const nextListElement = nextDocument.querySelector('[data-kl-blog-load-more-list]');

  if (!nextListElement) throw new Error('the next page carried no list');

  const nextButtonElement = nextDocument.querySelector('[data-kl-blog-load-more-button]');
  const nextHref = nextButtonElement ? nextButtonElement.getAttribute('href') : null;

  return {
    cardElements: Array.from(nextListElement.children),
    nextPageUrl: nextHref ? new URL(nextHref, pageUrl).toString() : null,
  };
}

function initBlogLoadMore(rootElement) {
  if (rootElement.dataset.klBlogLoadMoreReady === 'true') return;

  const sectionId = rootElement.dataset.klBlogLoadMore;
  const listElement = rootElement.querySelector('[data-kl-blog-load-more-list]');
  const buttonElement = rootElement.querySelector('[data-kl-blog-load-more-button]');
  const statusElement = rootElement.querySelector('[data-kl-blog-load-more-status]');

  // No button means the blog fits on one page. Nothing to fill, nothing to intercept.
  if (!buttonElement) return;

  if (!sectionId || !listElement) {
    console.debug('kl-blog-load-more: the section names no id, or has no list', { sectionId });
    return;
  }

  rootElement.dataset.klBlogLoadMoreReady = 'true';

  const statusTemplateText = rootElement.dataset.klBlogLoadMoreStatusText || '';
  const targetCount = Number(rootElement.dataset.klBlogLoadMoreTarget) || 1;

  let isLoading = false;
  let hasFailed = false;

  /*
    Appends whole pages until `wantedCount` cards have arrived, the blog runs out, or the
    request budget is spent. Returns how many were added; the caller decides what to say about
    it, which is the difference between a press and the quiet fill on arrival.
  */
  async function fillGrid(startPageUrl, wantedCount) {
    let requestUrl = startPageUrl;
    let addedCount = 0;
    let requestCount = 0;

    while (requestUrl && addedCount < wantedCount && requestCount < BLOG_LOAD_MORE_MAX_REQUESTS) {
      const page = await fetchBlogPage(requestUrl, sectionId);

      page.cardElements.forEach((cardElement) => listElement.appendChild(cardElement));

      addedCount += page.cardElements.length;
      requestUrl = page.nextPageUrl;
      requestCount += 1;
    }

    if (requestUrl) {
      buttonElement.setAttribute('href', requestUrl);
    } else {
      buttonElement.remove();
    }

    if (addedCount > 0) {
      /*
        Markup that arrives after load fires neither DOMContentLoaded nor
        shopify:section:load, so whatever injected it says where to look. Nothing on a blog
        card is behavioural today; the event is sent anyway because the next thing added to one
        will be, and a card appended here must not be the one card on the page that never
        starts.
      */
      document.dispatchEvent(
        new CustomEvent('kl:content:added', { detail: { root: listElement } })
      );
    }

    return addedCount;
  }

  async function runFill(startPageUrl, wantedCount, isAnnounced) {
    isLoading = true;
    buttonElement.classList.add(BLOG_LOAD_MORE_LOADING_CLASS);
    buttonElement.setAttribute('aria-disabled', 'true');

    try {
      const addedCount = await fillGrid(startPageUrl, wantedCount);

      if (isAnnounced && statusElement && statusTemplateText) {
        statusElement.textContent = getBlogStatusMessage(statusTemplateText, addedCount);
      }
    } catch (error) {
      console.debug('kl-blog-load-more: could not fill the grid', { startPageUrl, error });
      hasFailed = true;
    } finally {
      isLoading = false;

      // The button is gone once the blog has run out, and a removed node keeps no state.
      if (buttonElement.isConnected) {
        buttonElement.classList.remove(BLOG_LOAD_MORE_LOADING_CLASS);
        buttonElement.removeAttribute('aria-disabled');
      }
    }
  }

  buttonElement.addEventListener('click', (event) => {
    // Once a fill has failed, the link is the answer and this handler stays out of the way.
    if (hasFailed) return;
    if (isLoading) return;

    const nextPageUrl = buttonElement.href;

    if (!nextPageUrl) return;

    event.preventDefault();

    runFill(nextPageUrl, targetCount, true);
  });

  /*
    The quiet fill on arrival. The server rendered whatever survived the featured-post
    exclusion, which on the first page is routinely fewer than a full screen — four cards where
    ten were asked for. Nothing is announced, because nobody asked for anything yet.

    Not announced, and not run at all when the grid already came back full, which is every page
    but the first on most blogs.
  */
  const wantedOnArrival = getBlogCardsWanted(targetCount, listElement.children.length);

  if (wantedOnArrival > 0) runFill(buttonElement.href, wantedOnArrival, false);
}

document.querySelectorAll('[data-kl-blog-load-more]').forEach(initBlogLoadMore);

// Theme editor: a re-rendered section arrives with fresh, uninitialised markup.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-kl-blog-load-more]').forEach(initBlogLoadMore);
});

/*
  There is deliberately no kl:content:added listener here. Nothing on this page injects a grid:
  a search replaces the featured row with a sideways row of cards that carries no Load more of
  its own, and the fills above append into a list that is already running. A listener for an
  event that cannot arrive is a claim the code does not honour — add one back the day something
  does inject a grid, and not before.
*/
